/* eslint-disable no-param-reassign */
/* eslint-disable import/no-unresolved */
import React, {
  useState, useEffect, useMemo, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import getSymbolFromCurrency from 'currency-symbol-map';

import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import { getCountryOptions } from '../../../../services/countryService';
import { DOCUMENT_TYPES } from '../../../../constants/AppConstants';
import { getRegionalConfig } from '../../../../config';
import PaymentGatewayConnectionModelMC from '../../../models/PaymentGatewayConnectionModelMC';
import PaymentLinksCustomerData from './PaymentLinksCustomerData';
import PaymentLinksOrderData from './PaymentLinksOrderData';
import WavaModelMC from '../../../models/WavaModelMC';
import { getDocumentCode } from '../../../../util/DocumentCode';
import { sharingInformationService } from '../../../../services/sharingInformationService';
import { formatSimpleOrderMessage, uuid } from '../../../../util/utils';
import RightModalLayout from '../../../layouts/Modal/RightModalLayout';
import { visiblePhoneNumber } from '../../../../util/phoneNumber';
import {
  serializePaymentMethods,
  formatPaymentLinks,
  hasWavaMethods,
  togglePaymentMethodSelection
} from '../../../../services/paymentMethodsService';
import { generateExternalKey } from '../../../../util/externalKeyUtils';

const PaymentLinksModal = ({
  platform = 'whatsapp',
  customer = null
}) => {
  const { t } = useTranslation('Inbox');
  const { t: tCommon } = useTranslation('Common');
  const dispatch = useDispatch();
  const { retailer_info } = useSelector((reduxState) => reduxState.retailerUsersReducer);
  const { currentRetailerUser } = useSelector((reduxState) => reduxState.mainReducer);
  const phone = visiblePhoneNumber(customer?.phone_without_country_code || customer?.phone, currentRetailerUser);
  const onClose = useCallback(() => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  }, [dispatch]);

  const [currentStep, setCurrentStep] = useState(1);
  const [countryOptions, setCountryOptions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [saving, setSaving] = useState(false);

  const step1Schema = object().shape({
    email: string()
      .nullable()
      .test(
        'is-valid-email',
        tCommon('form.validations.email.validation_format'),
        (value) => !value || (typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      ),
    first_name: string().required(tCommon('form.validations.required')),
    last_name: string().required(tCommon('form.validations.required')),
    country_id: string().required(tCommon('form.validations.required')),
    phone: string()
      .nullable()
      .transform((value) => {
        // Convertir número a string si es necesario
        if (typeof value === 'number') {
          return String(value);
        }
        return value;
      })
      .test(
        'is-valid-phone',
        tCommon('form.validations.phone.validation_format'),
        (value, context) => {
          const { email } = context.parent;
          if (!value && !email) return false;
          if (!value) return true;

          const phoneStr = String(value);
          if (/[xX*]/.test(phoneStr)) return true;

          return /^\+?\d+$/.test(phoneStr);
        }
      )
  });

  const step1Methods = useForm({
    resolver: yupResolver(step1Schema),
    defaultValues: {
      email: customer?.email || '',
      first_name: customer?.first_name || '',
      last_name: customer?.last_name || '',
      country_id: customer?.country_id || ENV.COUNTRY_CODE || 'EC',
      phone: phone ? parseInt(phone.replace(/\D/g, ''), 10) || '' : '',
      id_type: customer?.id_type || 'cedula',
      id_number: customer?.id_number || ''
    }
  });

  const step2Methods = useForm({
    defaultValues: {
      amount: '',
      description: ''
    }
  });

  const documentTypeOptions = useMemo(() => DOCUMENT_TYPES.map((doc) => ({
    id: doc.value,
    label: doc.label
  })), []);

  useEffect(() => {
    const options = getCountryOptions();
    const formattedOptions = options.map((option) => ({
      ...option,
      id: option.value || option.code,
      label: option.label
    }));
    setCountryOptions(formattedOptions);
  }, []);

  useEffect(() => {
    PaymentGatewayConnectionModelMC.get().then((res) => {
      setPaymentMethods(serializePaymentMethods(res.gateways, retailer_info));
    });
  }, [retailer_info]);

  const handleSelected = (method) => {
    setPaymentMethods(togglePaymentMethodSelection(paymentMethods, method));
  };

  const onStep1Submit = useCallback(() => {
    setCurrentStep(2);
  }, []);

  const selectedPaymentMethods = paymentMethods.filter((p) => p.selected);
  const { currency } = getRegionalConfig();
  const currency_symbol = getSymbolFromCurrency(currency) || '$';
  const formattedDateTime = moment().local().locale('es').format('DD-MM-YYYY HH:mm');

  const formatMessageAndSend = useCallback((linkText) => {
    const orderData = step2Methods.getValues();
    let text = '';

    // Para virtual orders sin web_id, crear mensaje personalizado
    text = formatSimpleOrderMessage(
      { description: orderData.description, amount: orderData.amount },
      platform,
      currency_symbol,
      currency
    );

    if (linkText) {
      text += `\n*${t('Inbox:sendPaymentLinks.availablePaymentMethods')}*`;
      text += text ? `\n${linkText}` : linkText;
    }

    // Asegurar que siempre haya texto (al menos los links)
    if (!text && linkText) {
      text = linkText;
    }

    if (!text) {
      console.error('formatMessageAndSend - No text to send!');
      return;
    }

    // Crear payment_link_data para el mensaje de nota (similar a OrderNoteMessage)
    const cleanAmount = orderData.amount ? String(orderData.amount).replace(/,/g, '').replace(/[^\d.-]/g, '') : '';
    const amountNumber = cleanAmount ? Number(cleanAmount) : null;

    const paymentLinkData = {
      description: orderData.description || null,
      amount: amountNumber,
      currency,
      currency_symbol,
      payment_methods: selectedPaymentMethods.map((method) => method.name)
    };

    // Enviar evento para crear el mensaje de nota de payment link
    // MessageFormComponent procesará esto y creará el mensaje usando createPaymentLinkMessage
    sharingInformationService.setSubject({
      fillInputChatComponent: true,
      sendPaymentLinks: true,
      text,
      paymentLinkData
    });
    sharingInformationService.setSubject({});

    // Llenar el input directamente como lo hace fillInputText en WsChatMessages (old inbox)
    const messageInput = $('#divMessage');
    if (messageInput.length) {
      messageInput.text(text);

      // Habilitar el botón de enviar y maximizar el input
      sharingInformationService.setSubject({ enabledSendButton: true });
      sharingInformationService.setSubject({});

      setTimeout(() => {
        sharingInformationService.setSubject({ enabledSendButton: false });
        sharingInformationService.setSubject({});
      }, 100);

      // Disparar evento input para que maximizeInputText se ejecute
      messageInput.trigger('input');
    }

    // Cerrar el modal
    onClose();
  }, [step2Methods, platform, currency_symbol, currency, selectedPaymentMethods, onClose, t]);

  const handleSendPaymentLinks = useCallback(async () => {
    if (!selectedPaymentMethods.length) {
      if (typeof window !== 'undefined' && window.showtoast) {
        window.showtoast(t('sendPaymentLinks.selectAtLeastOnePlatform'));
      }
      return;
    }

    const customerData = step1Methods.getValues();
    const orderData = step2Methods.getValues();

    setSaving(true);

    try {
      let linkText = '';
      let wavaLink = null;

      if (hasWavaMethods(selectedPaymentMethods) && orderData.amount) {
        // Limpiar el amount antes de convertirlo a número
        const cleanAmount = String(orderData.amount).replace(/,/g, '').replace(/[^\d.-]/g, '');
        const amountNumber = cleanAmount ? Number(cleanAmount) : 0;

        // Crear orden virtual para generar link de Wava
        const virtualOrder = {
          amount: amountNumber,
          description: orderData.description || 'Pago genérico',
          user: {
            first_name: customerData.first_name,
            last_name: customerData.last_name,
            email: customerData.email,
            phone_number: String(customerData.phone || ''),
            country: customerData.country_id,
            id_number: customerData.id_number,
            id_type: getDocumentCode(customerData.country_id, customerData.id_type, { fallback: 'CI' })
          },
          redirect_link: '',
          order_key: generateExternalKey(uuid(), retailer_info.id)
        };

        wavaLink = await WavaModelMC.linkBy({
          link_data: virtualOrder
        }).then((res) => res.link);
      }

      linkText = formatPaymentLinks(selectedPaymentMethods, wavaLink, null);

      formatMessageAndSend(linkText);

      onClose();
    } catch (err) {
      if (typeof window !== 'undefined' && window.showtoast) {
        window.showtoast(err.message);
      }
    } finally {
      setSaving(false);
    }
  }, [selectedPaymentMethods, step1Methods, step2Methods, t, formatMessageAndSend, onClose, retailer_info.id]);

  // Modal configuration
  const modalConfig = useMemo(() => {
    if (currentStep === 1) {
      return { title: t('sendPaymentLinks.step1Of2'), subtitle: t('sendPaymentLinks.title') };
    }
    return { title: t('sendPaymentLinks.step2Of2'), subtitle: t('sendPaymentLinks.orderInformation') };
  }, [currentStep, t]);

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={modalConfig.subtitle}
        caption={modalConfig.title}
        closeAction={onClose}
        submitAction={currentStep === 1 ? step1Methods.handleSubmit(onStep1Submit) : handleSendPaymentLinks}
        submitLabel={currentStep === 1 ? t('sendPaymentLinks.continue') : t('sendPaymentLinks.sendLink')}
        submitDisabled={currentStep === 2 ? saving || !selectedPaymentMethods.length || !step2Methods.watch('amount') : false}
        cancelAction={currentStep === 1 ? onClose : () => setCurrentStep(1)}
        cancelLabel={t('sendPaymentLinks.back')}
      >
        {currentStep === 1 ? (
          <form onSubmit={step1Methods.handleSubmit(onStep1Submit)} className="tw-flex tw-flex-col tw-h-full">
            <PaymentLinksCustomerData
              formMethods={step1Methods}
              countryOptions={countryOptions}
              documentTypeOptions={documentTypeOptions}
            />
          </form>
        ) : (
          <PaymentLinksOrderData
            formMethods={step2Methods}
            paymentMethods={paymentMethods}
            selectedPaymentMethods={selectedPaymentMethods}
            handleSelected={handleSelected}
            currency={currency}
            currency_symbol={currency_symbol}
            formattedDateTime={formattedDateTime}
          />
        )}
      </RightModalLayout>
    </div>
  );
};

export default PaymentLinksModal;
